import moment from 'moment';

import {
  AustralianState,
  Address,
  YesNoAnswer,
  Excess,
  PaymentFrequency,
} from 'scenes/common/types';
import {
  MotorCoverType,
  MotorInsuranceType,
  OptionalExtra,
  MotorFunnelState,
  Driver,
  VehicleSearchType,
  Finance,
  MotorFlowType,
  VehicleManuallyEntered,
  VehicleFromSearch,
  InsuranceOption,
  DriverGender,
  MarketAgreedValue,
  ExcessType,
} from 'scenes/motor/types';
import {
  getApiData,
  hasData,
  getErrorReason,
  isBusinessError,
} from 'scenes/common/helpers/apiData';
import { defaultInsuranceOption } from 'scenes/motor/flows/quoteDetails/defaults';
import { getCampaignAdjustments } from './campaignSelectors';

export const getPolicyStartDate = (state: MotorFunnelState): string =>
  state.quoteDetails.policyStartDate;

export const yearsOld = (state: Partial<Driver>) =>
  state.dob ? moment().diff(moment(state.dob, 'DDMMYYYY'), 'years') : 0;

export const getDriverAge = (state: MotorFunnelState) =>
  yearsOld(state[state.flow] as Driver);

export const anyDriverUnder25 = (state: MotorFunnelState): boolean =>
  yearsOld(state.quoteDetails) < 25 ||
  state.quoteDetails.additionalDrivers.some((driver) => yearsOld(driver) < 25);

export const getPreselectedInsuranceType = (
  state: MotorFunnelState
): MotorInsuranceType => state.quoteDetails.preselectedInsuranceType;

export const getSelectedInsuranceType = (
  state: MotorFunnelState
): MotorInsuranceType => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).userSelections
      .insuranceType;
  }
  return getPreselectedInsuranceType(state);
};

export const isComprehensiveSelected = (state: MotorFunnelState): boolean =>
  getSelectedInsuranceType(state) === MotorInsuranceType.Comprehensive;

export const isThirdPartySelected = (state: MotorFunnelState): boolean =>
  getSelectedInsuranceType(state) === MotorInsuranceType.ThirdParty;

export const isCoverUnder25Included = (state: MotorFunnelState) =>
  anyDriverUnder25(state) || isThirdPartySelected(state);

export const getPreselectedCoverType = (
  state: MotorFunnelState
): MotorCoverType => state.quoteDetails.preselectedCoverType;

export const getSelectedExtras = (state: MotorFunnelState): string[] =>
  state.coverOptions.selectedExtras;

export const getIncludedExtras = (state: MotorFunnelState): string[] => [
  ...(anyDriverUnder25(state) ? [OptionalExtra.Under25 as string] : []),
  ...getCampaignAdjustments(state)
    .filter(({ percentageValue }) => percentageValue === 100)
    .map(({ type }) => type),
];

export const getExtras = (state: MotorFunnelState): string[] => [
  ...getSelectedExtras(state),
  ...getIncludedExtras(state),
];

export const isUnder25OptionalExtraSelected = (
  state: MotorFunnelState
): boolean => state.coverOptions.selectedExtras.includes(OptionalExtra.Under25);

export const getLicencePlate = (state: MotorFunnelState): string =>
  state.quoteDetails.licencePlate;

export const getVehicleFromSearch = (
  state: MotorFunnelState
): VehicleFromSearch => state.quoteDetails.vehicleFromSearch;

export const hasVehicleFromSearchData = (state: MotorFunnelState): boolean =>
  Boolean(state.quoteDetails.vehicleFromSearch.code);

export const getVehicleFromManualEntry = (state: MotorFunnelState) =>
  state.quoteDetails.vehicleManualSelectFields;

export const vehicleManuallySelected = (state: MotorFunnelState): boolean =>
  state.quoteDetails.vehicleSearchType === VehicleSearchType.ManualEntry;

export const getVehicleFromSearchDescription = (
  state: MotorFunnelState
): string => {
  const vehicle = getVehicleFromSearch(state);
  return `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim();
};

export const getManualVehicleDescription = (
  state: MotorFunnelState
): string => {
  const vehicle = state.quoteDetails.vehicleManualSelectFields;
  return `${vehicle.vehicleManufactureYear} ${vehicle.vehicleMake} ${vehicle.vehicleModel}`.trim();
};

export const getVehicleDescription = (state: MotorFunnelState): string =>
  vehicleManuallySelected(state)
    ? getManualVehicleDescription(state)
    : getVehicleFromSearchDescription(state);

export const isMainDriver = (state: MotorFunnelState): boolean =>
  state.quoteDetails.driverIndex === undefined ||
  state.quoteDetails.driverIndex < 0;

export const getName = (state: MotorFunnelState) => {
  const { driverIndex, additionalDrivers } = state.quoteDetails;
  const mainDriver = isMainDriver(state);
  // If unspecified, default to returning name of main driver
  if (mainDriver) {
    return {
      firstName: state.quoteDetails.firstName,
      lastName: state.quoteDetails.lastName,
    };
  }
  // Return empty strings if driver index is not defined
  const numDrivers = additionalDrivers ? additionalDrivers.length + 1 : 1;
  if (driverIndex === undefined || driverIndex >= numDrivers) {
    return {
      firstName: '',
      lastName: '',
    };
  }

  return {
    firstName: state.quoteDetails.additionalDrivers[driverIndex].firstName,
    lastName: state.quoteDetails.additionalDrivers[driverIndex].lastName,
  };
};

export const getGender = (state: MotorFunnelState): DriverGender =>
  (state[state.flow] as Driver).gender;

export const getDob = (state: MotorFunnelState): string =>
  (state[state.flow] as Driver).dob;

export const getYearsDriving = (state: MotorFunnelState): number =>
  (state[state.flow] as Driver).yearsDriving;

export const getClaimsMade = (state: MotorFunnelState): number =>
  (state[state.flow] as Driver).claimsMade;

export const getPayableClaims = (state: MotorFunnelState): number =>
  (state[state.flow] as Driver).payableClaims;

export const hasClaimsMade = (state: MotorFunnelState): boolean =>
  ((state[state.flow] as Driver).claimsMade ?? 0) > 0;

export const getIsUsedForBusiness = (state: MotorFunnelState): YesNoAnswer =>
  state.quoteDetails.isUsedForBusiness;

export const isVehicleUsedForBusiness = (state: MotorFunnelState): boolean =>
  getIsUsedForBusiness(state) === YesNoAnswer.Yes;

export const isVehicleUnderFinance = (state: MotorFunnelState): boolean =>
  state.quoteDetails.finance !== Finance.NoFinance;

export const getVehicleUnderFinance = (state: MotorFunnelState): Finance =>
  state.quoteDetails.finance;

export const getMainDriver = (
  state: MotorFunnelState
): Driver & { under25: boolean } => ({
  ...(state.quoteDetails as Driver),
  under25: yearsOld(state.quoteDetails) < 25,
});

export const getAdditionalDrivers = (
  state: MotorFunnelState
): Array<Driver & { under25: boolean }> =>
  state.quoteDetails.additionalDrivers.map((driver) => ({
    ...driver,
    under25: yearsOld(driver) < 25,
  }));

export const getAllDrivers = (state: MotorFunnelState): Driver[] => [
  getMainDriver(state),
  ...getAdditionalDrivers(state),
];

export const areAllDriversNamesProvided = (state: MotorFunnelState): boolean =>
  !getAllDrivers(state).find((driver) => !driver.firstName || !driver.lastName);

export const getNumberOfAdditionalDrivers = (state: MotorFunnelState): number =>
  getAdditionalDrivers(state).length;

export const hasAdditionalDrivers = (state: MotorFunnelState): boolean =>
  getNumberOfAdditionalDrivers(state) > 0;

export const getAddDriver = (state: MotorFunnelState): YesNoAnswer =>
  state.quoteDetails.addDriver;

export const hasAdditionalDriverClaimsMade = (
  state: MotorFunnelState
): boolean =>
  (getAdditionalDrivers(state)[getNumberOfAdditionalDrivers(state) - 1]
    ?.claimsMade ?? 0) > 0;

export const hasAddedDriverClaimsMade = (state: MotorFunnelState): boolean =>
  (state[MotorFlowType.AddDriver].claimsMade ?? 0) > 0;

export const getPrefilledCampaignId = (state: MotorFunnelState): string =>
  state.quoteDetails.prefilledCampaignId;

export const getColesCampaignCode = (state: MotorFunnelState): string =>
  state.quoteDetails.colesCampaignCode;

export const getPrefilledPromoCode = (state: MotorFunnelState): string =>
  state.quoteDetails.prefilledPromoCode;

export const getPromoCode = (state: MotorFunnelState): string =>
  state.quoteDetails.promoCode;

export const hasPromoCode = (state: MotorFunnelState): boolean =>
  getPromoCode(state).length !== 0;

export const hasFlybuys = (state: MotorFunnelState) =>
  state.quoteDetails.hasFlybuys;

export const isFlybuysMember = (state: MotorFunnelState): boolean =>
  state.quoteDetails.hasFlybuys === YesNoAnswer.Yes;

export const getFlybuysNumber = (state: MotorFunnelState): string =>
  (isFlybuysMember(state) && state.quoteDetails.flybuysNumber) || '';

export const getRecentInsurer = (state: MotorFunnelState): string =>
  state.quoteDetails.recentInsurer;

export const hasCoverLevel = (state: MotorFunnelState): boolean =>
  Boolean(state.quoteDetails.coverLevel);

export const getCoverLevel = (state: MotorFunnelState): string =>
  state.quoteDetails.coverLevel;

export const getOvernightParkingAddress = (state: MotorFunnelState): Address =>
  state.quoteDetails.overnightParkingAddress;

export const getVehicleSearchType = (
  state: MotorFunnelState
): VehicleSearchType => state.quoteDetails.vehicleSearchType;

export const isVehicleLookup = (state: MotorFunnelState): boolean =>
  state.quoteDetails.vehicleSearchType === VehicleSearchType.RegoLookup;

export const isVehicleManualEntry = (state: MotorFunnelState): boolean =>
  state.quoteDetails.vehicleSearchType === VehicleSearchType.ManualEntry;

export const getVehicleManuallyEntered = (
  state: MotorFunnelState
): VehicleManuallyEntered => state.quoteDetails.vehicleManuallyEntered;

export const getBusinessType = (state: MotorFunnelState): string =>
  state.quoteDetails.businessType;

export const getIsSmallBusiness = (state: MotorFunnelState): YesNoAnswer =>
  state.quoteDetails.isSmallBusiness;

export const isNswParkingAddress = (state: MotorFunnelState): boolean =>
  state.quoteDetails.overnightParkingAddress.state === AustralianState.NSW;

export const isSmallBusiness = (state: MotorFunnelState): boolean =>
  isNswParkingAddress(state) &&
  state.quoteDetails.isUsedForBusiness === YesNoAnswer.Yes &&
  state.quoteDetails.isSmallBusiness === YesNoAnswer.Yes;

export const getCoverForDriverUnder25 = (
  state: MotorFunnelState
): YesNoAnswer => state.quoteDetails.coverForDriversUnder25;

export const isUnder25CoverSelected = (state: MotorFunnelState): boolean =>
  getCoverForDriverUnder25(state) === YesNoAnswer.Yes;

export const getFinance = (state: MotorFunnelState): Finance =>
  state.quoteDetails.finance;

export const getVehicleFinancier = (state: MotorFunnelState): string =>
  state.quoteDetails.vehicleFinancier;

export const getVehicleManualSelectFields = (state: MotorFunnelState) =>
  state.quoteDetails.vehicleManualSelectFields;

export const getSelectedCoverType = (state: MotorFunnelState) => {
  if (state.coverOptions.selectedCoverType) {
    return state.coverOptions.selectedCoverType;
  }
  return state.quoteDetails.preselectedCoverType;
};

export const getCoverTypeByInsurance = (insuranceType: MotorInsuranceType) =>
  insuranceType === MotorInsuranceType.Comprehensive
    ? MotorCoverType.Comprehensive
    : MotorCoverType.ThirdPartyPropertyDamage;

export const getSelectedInsuranceOption = (
  state: MotorFunnelState
): InsuranceOption =>
  getApiData(state.apiResponses.quoteDetails)?.insuranceOptions?.find(
    (opt) => opt.coverType === getSelectedCoverType(state)
  ) ||
  getApiData(state.apiResponses.quoteDetails)?.insuranceOptions?.[0] ||
  defaultInsuranceOption;

// Retrieves quote number that's returned from a successful API
// call only (eg NOT from an error)
export const getQuoteNumberFromSuccess = (
  state: MotorFunnelState
): string | undefined => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).payload.policy.number;
  }
  return undefined;
};

export const getQuoteNumber = (state: MotorFunnelState): string => {
  const numberFromSuccessfulCall = getQuoteNumberFromSuccess(state);
  if (numberFromSuccessfulCall) {
    return numberFromSuccessfulCall;
  }
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).payload.policy.number;
  }
  if (isBusinessError(state.apiResponses.quoteDetails)) {
    return getErrorReason(state.apiResponses.quoteDetails)
      .quoteNumber as string;
  }
  return '';
};

export const getQuoteReference = (state: MotorFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).payload.policy?.reference ||
      ''
    );
  }
  return '';
};

export const hasQuoteNumber = (state: MotorFunnelState): boolean =>
  Boolean(getQuoteNumber(state));

export const hasQuoteNumberFromSuccessfulCall = (
  state: MotorFunnelState
): boolean => Boolean(getQuoteNumberFromSuccess(state));

export const getFirstInstalment = (state: MotorFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).recalculatedInstalments
        ?.firstInstalment ??
      getSelectedInsuranceOption(state)?.premium.firstInstalment ??
      ''
    );
  }
  return '';
};

export const getMonthlyInstalment = (state: MotorFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).recalculatedInstalments
        ?.monthlyInstalment ??
      getSelectedInsuranceOption(state)?.premium.monthlyInstalment ??
      ''
    );
  }
  return '';
};

export const getMarketValue = (state: MotorFunnelState) =>
  getApiData(state.apiResponses.quoteDetails).vehicleMarketValue;

export const getMarketOrAgreedValue = (
  state: MotorFunnelState
): MarketAgreedValue =>
  getApiData(state.apiResponses.quoteDetails)?.userSelections
    ?.marketOrAgreedValue;

export const isMarketValue = (state: MotorFunnelState): boolean =>
  getApiData(state.apiResponses.quoteDetails).userSelections
    .marketOrAgreedValue === MarketAgreedValue.Market;

export const getAgreedVehicleValue = (state: MotorFunnelState): string =>
  getApiData(state.apiResponses.quoteDetails)?.userSelections
    ?.agreedVehicleValue;

export const getReceiptNumber = (state: MotorFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).receiptNumber ?? '';
  }
  return '';
};

export const getVehicleManufactureYear = (state: MotorFunnelState): string => {
  if (vehicleManuallySelected(state)) {
    return getVehicleManuallyEntered(state).year;
  }
  return getVehicleFromSearch(state).year;
};

export const isOneYearOldOrLessVehicle = (state: MotorFunnelState): boolean =>
  Number(getVehicleManufactureYear(state)) >= moment().year() - 1;

export const getSelectedExcess = (state: MotorFunnelState): Excess => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).userSelections.excess;
  }
  return {
    id: '',
    type: ExcessType.Unspecified,
    amount: 0,
  };
};

export const getStandardExcesses = (state: MotorFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).excesses.filter(
      (excess) =>
        excess.type === ExcessType.Standard &&
        excess.name === getSelectedInsuranceOption(state).coverType
    );
  }
  return [];
};

export const getAdditionalExcesses = (state: MotorFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).excesses.filter(
      (excess) =>
        excess.type === ExcessType.Additional &&
        excess.name === getSelectedInsuranceOption(state).coverType
    );
  }
  return [];
};

export const getSaveQuoteEmailAddress = (state: MotorFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).saveQuoteEmailAddress || ''
    );
  }
  return '';
};

export const getValidInsuranceOptions = (state: MotorFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).insuranceOptions.filter(
      (option) =>
        option.insuranceType === getSelectedInsuranceType(state) &&
        option.coverType !== MotorCoverType.ComprehensivePlus
    );
  }
  return [];
};

export const getPreselectedPaymentFrequency = (
  state: MotorFunnelState
): PaymentFrequency => {
  if (
    [
      MotorFlowType.CoverOptions,
      MotorFlowType.QuoteDetails,
      MotorFlowType.AddDriver,
      MotorFlowType.AddPolicyHolder,
    ].includes(state.flow)
  ) {
    return state.coverOptions.preselectedPaymentFrequency;
  }
  if (state.flow === MotorFlowType.PolicyDetails) {
    return state.policyDetails.preselectedPaymentFrequency;
  }
  return PaymentFrequency.Unspecified;
};

export const isMonthlyPaymentPreselected = (state: MotorFunnelState): boolean =>
  getPreselectedPaymentFrequency(state) === PaymentFrequency.Monthly;

export const getPreselectedPrice = (state: MotorFunnelState): string => {
  const {
    premium: { annualPrice, monthlyInstalment },
  } = getSelectedInsuranceOption(state);
  return isMonthlyPaymentPreselected(state) ? monthlyInstalment : annualPrice;
};

export const getDriverType = (flowType: MotorFlowType): string => {
  switch (flowType) {
    case MotorFlowType.QuoteDetails:
      return 'main';
    case MotorFlowType.AddDriver:
      return 'additional';
    default:
      return '';
  }
};
